import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  LinearProgress,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { AgentInlineSelect } from './ai-agent-ui';
import { apiService } from '../services/apiService';
import { useProjects } from '../context/ProjectContext';
import type { AgentMessageResponse } from '../types/agent';
import {
  normalizeAgentSwaggerUrl,
  validateAgentSwaggerUrl,
} from '../utils/agentEnvironmentUtils';
import {
  dispatchEnvironmentsChanged,
} from '../utils/environmentVariablesEvents';
import { isAgentFailureMessage } from '../utils/agentChatMessageStyle';
import {
  advanceSwaggerImportSteps,
  buildFinalSwaggerImportSteps,
  createInitialSwaggerImportSteps,
  swaggerImportStepsFromJobProgress,
  SWAGGER_IMPORT_COMPLETE_PAUSE_MS,
  SWAGGER_IMPORT_STEP_ADVANCE_MS,
  type AgentSwaggerImportStep,
  type SwaggerImportEnvironmentOutcome,
  type SwaggerImportLiveCounts,
  type SwaggerImportStats,
} from '../utils/agentSwaggerImportProgress';
import { subscribeSwaggerImportJob } from '../utils/agentSwaggerImportTracker';

export type AgentWidgetImportSwaggerFormProps = {
  chatId?: number | null;
  defaultProjectId?: number | '';
  defaultProjectName?: string;
  defaultSwaggerUrl?: string;
  onClose: () => void;
  onUserMessage: (text: string) => void;
  onAgentReply: (res: AgentMessageResponse) => void;
  onChatIdChange?: (chatId: number) => void;
  onLoadingChange?: (loading: boolean) => void;
};

function coerceImportCount(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
}

function resolveWidgetImportStats(res: AgentMessageResponse): SwaggerImportStats | null {
  const fromPayload = res.importStats;
  if (!fromPayload) return null;
  return {
    collections: coerceImportCount(fromPayload.collections),
    requests: coerceImportCount(fromPayload.requests),
    collectionsSkipped: coerceImportCount(fromPayload.collectionsSkipped),
    requestsSkipped: coerceImportCount(fromPayload.requestsSkipped),
    collectionsInSpec: coerceImportCount(fromPayload.collectionsInSpec),
    requestsInSpec: coerceImportCount(fromPayload.requestsInSpec),
    dataSets: coerceImportCount(fromPayload.dataSets),
    assertions: coerceImportCount(fromPayload.assertions),
    environmentOutcome: fromPayload.environmentOutcome as
      | SwaggerImportEnvironmentOutcome
      | undefined,
  };
}

function WidgetSwaggerImportProgressList({
  steps,
  progressPercent,
  progressLabel,
  liveCounts,
}: {
  steps: AgentSwaggerImportStep[];
  progressPercent?: number;
  progressLabel?: string;
  liveCounts?: SwaggerImportLiveCounts;
}) {
  const showBar = typeof progressPercent === 'number' && progressPercent >= 0;
  const collectionsCreated = liveCounts?.collectionsCreated ?? 0;
  const requestsCreated = liveCounts?.requestsCreated ?? 0;
  const dataSetsCreated = liveCounts?.dataSetsCreated ?? 0;
  const assertionsCreated = liveCounts?.assertionsCreated ?? 0;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 1 }}>
      {showBar && (
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.35 }}>
            {progressLabel?.trim() || 'Import in progress…'}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={Math.max(0, Math.min(100, progressPercent))}
            sx={{ height: 6, borderRadius: 1 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, display: 'block' }}>
            {Math.round(progressPercent)}% complete
          </Typography>
          <Box
            sx={{
              mt: 0.5,
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              columnGap: 1,
              rowGap: 0.15,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Collections created: {collectionsCreated}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Requests created: {requestsCreated}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Data sets created: {dataSetsCreated}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Assertions created: {assertionsCreated}
            </Typography>
          </Box>
        </Box>
      )}
      {steps.map((step) => (
        <Box key={step.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
          {step.status === 'done' && step.tone === 'warning' ? (
            <WarningAmberIcon fontSize="small" color="warning" sx={{ mt: 0.1 }} />
          ) : step.status === 'done' ? (
            <CheckCircleIcon fontSize="small" color="success" sx={{ mt: 0.1 }} />
          ) : step.status === 'running' ? (
            <CircularProgress size={12} sx={{ mt: 0.35, flexShrink: 0 }} />
          ) : (
            <Box
              sx={{
                width: 12,
                height: 12,
                mt: 0.35,
                borderRadius: '50%',
                border: '2px solid',
                borderColor: 'divider',
                flexShrink: 0,
              }}
            />
          )}
          <Typography
            variant="caption"
            color={
              step.status === 'done' && step.tone === 'warning'
                ? 'warning.main'
                : step.status === 'done'
                  ? 'success.main'
                  : step.status === 'running'
                    ? 'text.primary'
                    : 'text.secondary'
            }
          >
            {step.label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

const AgentWidgetImportSwaggerForm: React.FC<AgentWidgetImportSwaggerFormProps> = ({
  chatId,
  defaultProjectId = '',
  defaultProjectName = '',
  defaultSwaggerUrl = '',
  onClose,
  onUserMessage,
  onAgentReply,
  onChatIdChange,
  onLoadingChange,
}) => {
  const { setSelectedProject, refreshProjects } = useProjects();
  const [availableProjects, setAvailableProjects] = useState<Array<{ id: number; name: string }>>([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [selectedProjectId, setSelectedProjectId] = useState<number | ''>('');
  const [swaggerUrl, setSwaggerUrl] = useState(defaultSwaggerUrl);
  const [swaggerUrlError, setSwaggerUrlError] = useState<string | null>(
    defaultSwaggerUrl.trim() ? validateAgentSwaggerUrl(defaultSwaggerUrl) : null,
  );
  const [submitting, setSubmitting] = useState(false);
  const [importSteps, setImportSteps] = useState<AgentSwaggerImportStep[] | null>(null);
  const [progressPercent, setProgressPercent] = useState<number | undefined>(undefined);
  const [progressLabel, setProgressLabel] = useState<string | undefined>(undefined);
  const [liveCounts, setLiveCounts] = useState<SwaggerImportLiveCounts>({});
  const importStepsLockedRef = useRef(false);

  const mergeProjectOption = useCallback(
    (list: Array<{ id: number; name: string }>) => {
      if (defaultProjectId === '' || Number.isNaN(Number(defaultProjectId))) {
        return list;
      }
      const id = Number(defaultProjectId);
      if (list.some((p) => p.id === id)) {
        return list;
      }
      const name = defaultProjectName.trim() || `Project ${id}`;
      return [...list, { id, name }].sort((a, b) => a.name.localeCompare(b.name));
    },
    [defaultProjectId, defaultProjectName],
  );

  const loadProjects = useCallback(async () => {
    setLoadingProjects(true);
    try {
      await refreshProjects();
      const fetched = await apiService.getProjects();
      const mapped = fetched.map((p) => ({ id: p.id, name: p.name }));
      setAvailableProjects(mergeProjectOption(mapped));
    } catch {
      if (defaultProjectId !== '' && !Number.isNaN(Number(defaultProjectId))) {
        setAvailableProjects(
          mergeProjectOption([
            {
              id: Number(defaultProjectId),
              name: defaultProjectName.trim() || `Project ${defaultProjectId}`,
            },
          ]),
        );
      } else {
        setAvailableProjects([]);
      }
    } finally {
      setLoadingProjects(false);
    }
  }, [defaultProjectId, defaultProjectName, mergeProjectOption, refreshProjects]);

  useEffect(() => {
    void loadProjects();
  }, [loadProjects]);

  useEffect(() => {
    if (loadingProjects) return;
    if (defaultProjectId !== '' && availableProjects.some((p) => p.id === defaultProjectId)) {
      setSelectedProjectId(defaultProjectId);
      return;
    }
    if (availableProjects.length === 1) {
      setSelectedProjectId(availableProjects[0].id);
    }
  }, [defaultProjectId, availableProjects, loadingProjects]);

  useEffect(() => {
    if (!submitting || importSteps == null) return undefined;
    if (typeof progressPercent === 'number') return undefined;
    const timer = window.setInterval(() => {
      if (importStepsLockedRef.current) return;
      setImportSteps((prev) => (prev ? advanceSwaggerImportSteps(prev) : prev));
    }, SWAGGER_IMPORT_STEP_ADVANCE_MS);
    return () => window.clearInterval(timer);
  }, [submitting, importSteps == null, progressPercent]);

  useEffect(() => {
    if (!submitting) return undefined;
    return subscribeSwaggerImportJob((job) => {
      if (typeof job.progressPercent === 'number') {
        setProgressPercent(job.progressPercent);
        if (!importStepsLockedRef.current) {
          setImportSteps(swaggerImportStepsFromJobProgress(job.progressPercent, job));
        }
      }
      if (job.progressLabel) {
        setProgressLabel(job.progressLabel);
      }
      setLiveCounts({
        collectionsCreated: job.collectionsCreated ?? 0,
        requestsCreated: job.requestsCreated ?? 0,
        dataSetsCreated: job.dataSetsCreated ?? 0,
        assertionsCreated: job.assertionsCreated ?? 0,
      });
    });
  }, [submitting]);

  const selectValue = useMemo(() => {
    if (selectedProjectId === '') return '';
    return availableProjects.some((p) => p.id === selectedProjectId) ? selectedProjectId : '';
  }, [availableProjects, selectedProjectId]);

  const handleImport = async () => {
    const urlInput = swaggerUrl.trim();
    const urlValidationError = validateAgentSwaggerUrl(urlInput);
    setSwaggerUrlError(urlValidationError);
    if (!urlInput || !selectedProjectId || urlValidationError) return;

    const url = normalizeAgentSwaggerUrl(urlInput);
    const selected = availableProjects.find((p) => p.id === selectedProjectId);
    const userText = `Import Swagger URL ${url} into project "${selected?.name || 'selected project'}"`;
    onUserMessage(userText);
    importStepsLockedRef.current = false;
    setImportSteps(createInitialSwaggerImportSteps());
    setProgressPercent(undefined);
    setProgressLabel(undefined);
    setLiveCounts({});
    setSubmitting(true);
    onLoadingChange?.(true);
    try {
      let res = await apiService.agentMessage({
        action: 'create_project_import',
        chatId: chatId ?? undefined,
        message: userText,
        payload: { projectId: selectedProjectId, swaggerUrl: url },
      });
      const swaggerJobId =
        typeof res?.importStats?.swaggerImportJobId === 'string'
          ? res.importStats.swaggerImportJobId
          : null;
      if (swaggerJobId) {
        const { trackSwaggerImportJob, waitForSwaggerImportJob } = await import(
          '../utils/agentSwaggerImportTracker'
        );
        const parseId = (value: unknown): number | null => {
          if (typeof value === 'number' && !Number.isNaN(value) && value > 0) return value;
          if (typeof value === 'string' && value.trim() !== '') {
            const n = Number(value);
            if (!Number.isNaN(n) && n > 0) return n;
          }
          return null;
        };
        trackSwaggerImportJob(
          swaggerJobId,
          parseId(res.importStats?.chatId) ?? parseId(res.state?.chatId) ?? chatId ?? null,
        );
        const job = await waitForSwaggerImportJob(swaggerJobId);
        if (job.result) {
          res = job.result;
        } else if (job.status === 'FAILED') {
          res = {
            reply: job.error || 'Import failed.',
            error: job.error || 'Import failed.',
            state: res.state ?? null,
          };
        }
      }
      if (res.state?.chatId != null) {
        onChatIdChange?.(res.state.chatId);
      }
      if (res.state?.projectId != null) {
        try {
          const list = await apiService.getProjects();
          const match = list.find((p) => p.id === res.state?.projectId);
          if (match) setSelectedProject(match);
        } catch {
          /* ignore */
        }
      }
      const failed = Boolean(res.error) || isAgentFailureMessage(res.reply || '', res.actions);
      if (!failed) {
        const stats = resolveWidgetImportStats(res);
        const finalSteps = buildFinalSwaggerImportSteps(stats);
        importStepsLockedRef.current = true;
        setImportSteps(finalSteps);
        await new Promise((resolve) => window.setTimeout(resolve, SWAGGER_IMPORT_COMPLETE_PAUSE_MS));
      }
      onAgentReply(res);
      if (!failed) {
        const projectId =
          typeof res.state?.projectId === 'number' ? res.state.projectId : Number(selectedProjectId);
        if (!Number.isNaN(projectId) && projectId > 0) {
          dispatchEnvironmentsChanged({
            projectId,
            environmentId:
              typeof res.state?.environmentId === 'number' ? res.state.environmentId : undefined,
            source: 'widget',
          });
        }
        onClose();
      }
    } finally {
      setSubmitting(false);
      importStepsLockedRef.current = false;
      setImportSteps(null);
      onLoadingChange?.(false);
    }
  };

  return (
    <Box
      sx={{
        mt: 1,
        mb: 0.5,
        p: 1.25,
        borderRadius: 2,
        bgcolor: 'rgba(255,255,255,0.95)',
        border: '1px solid rgba(15,23,42,0.12)',
      }}
    >
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
        Import Swagger / OpenAPI
      </Typography>
      <Box sx={{ mb: 1 }}>
        <AgentInlineSelect
          id="widget-import-project"
          label="Project"
          fullWidth
          value={selectValue === '' ? '' : selectValue}
          disabled={submitting || loadingProjects}
          emptyLabel={loadingProjects ? 'Loading projects…' : 'No projects found'}
          options={availableProjects.map((project) => ({
            value: project.id,
            label: project.name,
          }))}
          onChange={(raw) => {
            const parsed = Number(raw);
            setSelectedProjectId(Number.isNaN(parsed) || raw === '' ? '' : parsed);
          }}
        />
      </Box>
      <TextField
        label="Swagger URL"
        size="small"
        fullWidth
        value={swaggerUrl}
        onChange={(e) => {
          const next = e.target.value;
          setSwaggerUrl(next);
          setSwaggerUrlError(validateAgentSwaggerUrl(next));
        }}
        onBlur={() => setSwaggerUrlError(validateAgentSwaggerUrl(swaggerUrl))}
        placeholder="https://api.example.com/v3/api-docs"
        error={!!swaggerUrlError}
        helperText={swaggerUrlError || undefined}
        disabled={submitting}
        sx={{ mb: 1 }}
      />
      {!loadingProjects && availableProjects.length === 0 && (
        <Typography variant="caption" color="error.main" sx={{ display: 'block', mb: 1 }}>
          Create a project first, then import Swagger.
        </Typography>
      )}
      {importSteps && (
        <WidgetSwaggerImportProgressList
          steps={importSteps}
          progressPercent={progressPercent}
          progressLabel={progressLabel}
          liveCounts={liveCounts}
        />
      )}
      <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'flex-end' }}>
        <Button size="small" onClick={onClose} disabled={submitting} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={() => void handleImport()}
          disabled={
            submitting ||
            loadingProjects ||
            !swaggerUrl.trim() ||
            !selectedProjectId ||
            !!swaggerUrlError ||
            !!validateAgentSwaggerUrl(swaggerUrl)
          }
          sx={{ textTransform: 'none' }}
        >
          {submitting ? <CircularProgress size={16} color="inherit" /> : 'Import'}
        </Button>
      </Box>
      <Divider sx={{ mt: 1.25 }} />
    </Box>
  );
};

export default AgentWidgetImportSwaggerForm;
